import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { useActionStreamStore } from '../../stores/actionStream.store';
import {
  computeUpcoming, toUnifiedRows, invoicesQueryOptions, quotesQueryOptions, SCADENZIARIO_DAYS,
} from '../../lib/scadenziario';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { ActionItemRow } from './ActionItemRow';
import { useActionStream, type ActionBand, type ActionBandGroup } from './useActionStream';

/**
 * ActionStream — lista azionabile della pagina "Oggi".
 *
 * Sezioni per banda di urgenza (CRITICO / OGGI / IN SETTIMANA); la banda 3
 * "QUANDO PUOI" è collassata di default dietro una chip con conteggio, e una
 * chip "Posticipati (n)" risveglia in blocco gli item rimandati.
 *
 * Tastiera:
 *   J / K (e frecce ↑↓) — roving tabindex tra le righe (gestito qui);
 *   Invio / E / H / O   — gestiti dalla riga focalizzata (ActionItemRow);
 *   J/K funzionano anche da fuori come "entry point" (portano il focus sulla
 *   riga attiva), con guardie: mai dentro input/textarea/contenteditable né
 *   con un modale aperto.
 *
 * Rimozione ottimistica: hide() marca la riga in uscita (transizione 180ms)
 * e committa dopo EXIT_MS; unhide() è il rollback (errore o Annulla) e, se
 * arriva prima del commit, annulla anche l'effetto differito (`after`).
 *
 * `compact`: variante per la rail/spazi stretti — righe più basse, niente
 * sub e niente pill "n nuovi" (vedi CSS .action-stream[data-compact]).
 */

// Etichette di sezione della UI (più asciutte di BAND_LABEL; uppercase via CSS)
const HEADER_LABEL: Record<ActionBand, string> = {
  0: 'Critico',
  1: 'Oggi',
  2: 'In settimana',
  3: 'Quando puoi',
};

// Un filo oltre i 180ms della transizione d'uscita (.as-row--leaving)
const EXIT_MS = 200;

/** True se i tasti globali vanno ignorati (digitazione in corso o modale aperto). */
function keyboardBlocked(target: EventTarget | null): boolean {
  const t = target as HTMLElement | null;
  if (t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable || !!t.closest('[contenteditable="true"]'))) return true;
  // copre modali zustand, ConfirmDialog e CommandPalette (tutti su .modal-backdrop)
  if (useModalStore.getState().kind || document.querySelector('.modal-backdrop')) return true;
  return false;
}

export function ActionStream({ compact }: { compact?: boolean }) {
  const { items, bands, counts, isLoading } = useActionStream();
  const user = useAuthStore((s) => s.user);
  const snoozedMap = useActionStreamStore((s) => s.snoozed);
  const wakeKey = useActionStreamStore((s) => s.wake);

  const listRef = useRef<HTMLDivElement>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [fyiOpen, setFyiOpen] = useState(false); // banda 3 collassata di default
  const [newCount, setNewCount] = useState(0);

  // ── Rimozione ottimistica: leaving (in transizione) → hidden (fuori) ──
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set());
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const leavingRef = useRef<Set<string>>(new Set());

  const hide = useCallback((key: string, after?: () => void) => {
    leavingRef.current = new Set(leavingRef.current).add(key);
    setLeaving(leavingRef.current);
    window.setTimeout(() => {
      // Annulla arrivato prima della fine transizione? Non committare nulla.
      if (!leavingRef.current.has(key)) return;
      const next = new Set(leavingRef.current);
      next.delete(key);
      leavingRef.current = next;
      setLeaving(next);
      setHidden((prev) => new Set(prev).add(key));
      after?.();
    }, EXIT_MS);
  }, []);

  const unhide = useCallback((key: string) => {
    if (leavingRef.current.has(key)) {
      const next = new Set(leavingRef.current);
      next.delete(key);
      leavingRef.current = next;
      setLeaving(next);
    }
    setHidden((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // ── Bande visibili (filtrate dalle rimozioni ottimistiche locali) ──
  const visibleBands = useMemo(
    () => bands.map((b) => ({ ...b, items: b.items.filter((i) => !hidden.has(i.key)) })),
    [bands, hidden],
  );
  const actionable = visibleBands.filter((b) => b.band <= 2);
  const fyiBand = visibleBands.find((b) => b.band === 3);
  const fyiCount = fyiBand?.items.length ?? 0;
  const allEmpty = actionable.every((b) => b.items.length === 0);

  // Roving tabindex: la riga attiva è quella focalizzata per ultima (o la prima)
  const orderedKeys = useMemo(
    () => visibleBands.flatMap((b) => (b.band === 3 && !fyiOpen ? [] : b.items.map((i) => i.key))),
    [visibleBands, fyiOpen],
  );
  const effectiveActive = activeKey && orderedKeys.includes(activeKey) ? activeKey : orderedKeys[0] ?? null;

  // ── Pill "n nuovi": item comparsi DOPO la prima fotografia post-load ──
  const seenRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (isLoading) return; // la baseline si scatta solo a dati pronti
    const keys = items.map((i) => i.key);
    if (!seenRef.current) { seenRef.current = new Set(keys); return; }
    let fresh = 0;
    for (const k of keys) {
      if (!seenRef.current.has(k)) { fresh += 1; seenRef.current.add(k); }
    }
    if (fresh > 0) setNewCount((n) => n + fresh);
  }, [items, isLoading]);

  const dismissNew = () => {
    setNewCount(0);
    listRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  // ── Tastiera: J/K e frecce muovono il focus tra le righe ──
  const moveFocus = useCallback((dir: 1 | -1, from: EventTarget | null) => {
    const root = listRef.current;
    if (!root) return;
    const rows = Array.from(root.querySelectorAll<HTMLElement>('[data-as-row]:not(.as-row--leaving)'));
    if (!rows.length) return;
    const current = from instanceof HTMLElement ? from.closest<HTMLElement>('[data-as-row]') : null;
    const idx = current ? rows.indexOf(current) : -1;
    const next = rows[idx < 0 ? 0 : Math.min(rows.length - 1, Math.max(0, idx + dir))];
    next?.focus();
  }, []);

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (keyboardBlocked(e.target)) return;
    const k = e.key.toLowerCase();
    if (k === 'j' || e.key === 'ArrowDown') { e.preventDefault(); moveFocus(1, e.target); }
    else if (k === 'k' || e.key === 'ArrowUp') { e.preventDefault(); moveFocus(-1, e.target); }
  };

  // Entry point globale: J/K da fuori lista portano il focus sulla riga attiva.
  // (Le frecce restano allo scroll di pagina quando il focus è altrove.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k !== 'j' && k !== 'k') return;
      if (keyboardBlocked(e.target)) return;
      const root = listRef.current;
      if (!root || (e.target instanceof Node && root.contains(e.target))) return; // dentro ci pensa onListKeyDown
      const row = root.querySelector<HTMLElement>('[data-as-row][tabindex="0"]')
        ?? root.querySelector<HTMLElement>('[data-as-row]');
      if (row) { e.preventDefault(); row.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Chip "Posticipati (n)": risveglia in blocco gli item rimandati ──
  const wakeAll = () => {
    for (const key of Object.keys(snoozedMap)) {
      wakeKey(key);
      unhide(key); // se era stata nascosta localmente prima dello snooze
    }
  };

  // ── Empty state: prossima scadenza dallo scadenziario (stessa cache) ──
  const canFatturazione = useMemo(() => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === ModuleKey.FATTURAZIONE)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.READ);
  }, [user]);
  const { data: invoiceData } = useQuery({ ...invoicesQueryOptions, enabled: canFatturazione });
  const { data: quoteData } = useQuery({ ...quotesQueryOptions, enabled: canFatturazione });
  const nextDue = useMemo(() => {
    if (!invoiceData || !quoteData) return null;
    const now = Date.now();
    const next = computeUpcoming(toUnifiedRows(quoteData.items ?? [], invoiceData.items ?? []))
      .find((r) => r.secondaryDate && new Date(r.secondaryDate).getTime() > now);
    if (!next) return null;
    const days = Math.ceil((new Date(next.secondaryDate!).getTime() - now) / 86400000);
    return { number: next.number, days };
  }, [invoiceData, quoteData]);

  const renderBand = (b: ActionBandGroup | undefined) => {
    if (!b || b.items.length === 0) return null;
    return (
      <section className="as-band" key={b.band} aria-label={HEADER_LABEL[b.band]}>
        <div className="as-band-hd">
          <span>{HEADER_LABEL[b.band]}</span>
          <span className="as-band-count">{b.items.length}</span>
        </div>
        {b.items.map((it) => (
          <ActionItemRow
            key={it.key}
            item={it}
            active={it.key === effectiveActive}
            leaving={leaving.has(it.key)}
            onActivate={setActiveKey}
            hide={hide}
            unhide={unhide}
          />
        ))}
      </section>
    );
  };

  if (isLoading && items.length === 0) {
    return (
      <div className="action-stream" data-compact={compact || undefined}>
        <div className="skeleton-col pt-1">
          <Skeleton size="block" h={56} />
          <Skeleton size="block" h={56} />
          <Skeleton size="block" h={56} />
        </div>
      </div>
    );
  }

  const chips = (fyiCount > 0 || counts.snoozed > 0) && (
    <div className="as-chips">
      {fyiCount > 0 && (
        <Chip tone={fyiOpen ? 'active' : 'ghost'} onClick={() => setFyiOpen((v) => !v)}>
          <Icon name="bell" size={11} /> Quando puoi · {fyiCount}
        </Chip>
      )}
      {counts.snoozed > 0 && (
        <Chip tone="ghost" onClick={wakeAll}>
          {/* "Posticipati · 3" e non "(3)": nelle chip il conto lo separa il
              punto mediano, come "Quando puoi · 6" qui accanto e come tutte
              le altre chip dell'app. Erano due modi di scrivere la stessa
              cosa a quattro pixel di distanza. */}
          <Icon name="clock" size={11} /> Posticipati · {counts.snoozed}
        </Chip>
      )}
    </div>
  );

  return (
    <div className="action-stream" data-compact={compact || undefined} ref={listRef} onKeyDown={onListKeyDown}>
      {!compact && newCount > 0 && (
        <button className="as-new-pill" onClick={dismissNew}>
          <Icon name="arrowUp" size={11} /> {newCount} nuov{newCount === 1 ? 'o' : 'i'}
        </button>
      )}
      {allEmpty ? (
        <EmptyState
          title="Tutto sotto controllo."
          icon="check"
          description={nextDue
            ? `Prossima scadenza: ${nextDue.number ?? 's/n'} tra ${nextDue.days} ${nextDue.days === 1 ? 'giorno' : 'giorni'}`
            : `Nessuna scadenza nei prossimi ${SCADENZIARIO_DAYS} giorni.`}
        />
      ) : (
        actionable.map((b) => renderBand(b))
      )}
      {fyiOpen && renderBand(fyiBand)}
      {chips}
    </div>
  );
}
